import React, {useEffect, /* useMemo, */ useState} from 'react'
import PropTypes from 'prop-types'
import {Stack, HStack, Text, useMultiStyleConfig, Link, Flex} from '@chakra-ui/react'
import Checkbox from '../checkbox'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {
    useShopperBasketsMutation,
    useShopperBasketsMutationHelper,
} from '@salesforce/commerce-sdk-react'
import {InfoIcon} from '../icons'
import {buildWarrantyName} from '../../utils/product-utils'
import LoadingSpinner from '../loading-spinner'

const WarrantySectionCart = ({warranties, product, warrantyTermsAndConditionsLink = null}) => {
    const styles = useMultiStyleConfig('WarrantySectionCart', {})
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const removeItemFromBasket = useShopperBasketsMutation('removeItemFromBasket')
    const actualWarranty = basket?.productItems?.find(
        (li) => li?.c_UUIDWarranty === product?.c_UUIDWarranty && li?.c_isWarranty === true,
    )
    const actualWarrantyId = actualWarranty?.productId
    const [selectedWarrantyId, setSelectedWarrantyId] = useState(actualWarrantyId || null)
    const [isBusy, setIsBusy] = useState(false)
    const addWarranty = async (warrantySkuId) => {
        const parentProductId = product?.productId || product?.id
        const item = {
            productId: warrantySkuId,
            quantity: 1,
            c_isWarranty: true,
            c_parentProductId: parentProductId,
        }
        await addItemToNewOrExistingBasket([item])
    }
    const hasBonusWarranty = !!(
        product?.c_UUIDWarranty &&
        basket?.productItems?.some(
            (li) =>
                li?.c_isWarranty === true &&
                li?.bonusProductLineItem === true &&
                li?.c_UUIDWarranty === product.c_UUIDWarranty,
        )
    )
    const isLocked = hasBonusWarranty

    const removeWarranty = async () => {
        if (!actualWarranty?.itemId || !basket?.basketId) return
        await removeItemFromBasket.mutateAsync({
            parameters: {basketId: basket.basketId, itemId: actualWarranty.itemId},
        })
    }
    const updateWarranty = async (newWarrantySkuId) => {
        if (!actualWarranty?.itemId || !basket?.basketId || !product?.c_UUIDWarranty) return

        const item = {
            productId: newWarrantySkuId,
            quantity: 1,
            c_isWarranty: true,
            c_UUIDWarranty: product.c_UUIDWarranty,
        }

        await updateItemInBasketMutation.mutateAsync({
            parameters: {basketId: basket.basketId, itemId: actualWarranty.itemId},
            body: item,
        })
    }
    // Toggle single-select
    const onToggle = async (warrantySkuId, checked) => {
        if (isBusy || isLocked) return
        setIsBusy(true)
        try {
            if (!checked && selectedWarrantyId === warrantySkuId) {
                await removeWarranty()
                setSelectedWarrantyId(null)
                return
            }

            if (actualWarrantyId && actualWarrantyId !== warrantySkuId) {
                await updateWarranty(warrantySkuId)
            } else if (!actualWarrantyId) {
                await addWarranty(warrantySkuId)
            }

            setSelectedWarrantyId(warrantySkuId)
        } finally {
            setIsBusy(false)
        }
    }
    useEffect(() => {
        setSelectedWarrantyId(actualWarrantyId || null)
    }, [actualWarrantyId, basket?.productItems])
    // Only show if there are warranties available
    if (product?.c_isWarranty || !warranties || warranties?.length === 0) {
        return null
    }

    return (
        <>
            {isBusy && <LoadingSpinner />}
            <Flex {...styles.outterContainer}>
                <Flex {...styles.container}>
                    {warrantyTermsAndConditionsLink && (
                        <Flex {...styles.termsAndConditionsContainer}>
                            <InfoIcon boxSize={'iconsSize.small'} />
                            <Link
                                href={warrantyTermsAndConditionsLink}
                                isExternal
                                _hover={{textDecoration: 'none'}}
                                textDecoration="underline"
                            >
                                <FormattedMessage
                                    defaultMessage="Termini e condizioni"
                                    id="warranty_selector.warranty_terms_and_conditions_link"
                                />
                            </Link>
                        </Flex>
                    )}
                </Flex>
                {/*             {isLocked && (
                <Text {...styles?.description}>
                    <FormattedMessage
                        id="warranty_section.description.locked"
                        defaultMessage="This warranty is already included with your purchase."
                    />
                </Text>
            )} */}
                <Stack {...styles.outterRadioContainer}>
                    {warranties.map((warranty) =>
                        warranty.options.map((opt) => {
                            const id = opt.id || opt.productId
                            const name = buildWarrantyName(
                                warranty.groupName,
                                opt.serviceDuration,
                                formatMessage,
                            )
                            const price = opt.price
                            const isChecked = selectedWarrantyId === id
                            return (
                                <HStack key={id} {...styles.radioContainer}>
                                    <Flex {...styles.itemContainer}>
                                        <Checkbox
                                            isChecked={isChecked}
                                            isDisabled={isLocked || isBusy}
                                            onChange={(e) => onToggle(id, e.target.checked)}
                                        />
                                        <Flex {...styles.textContainer}>
                                            <Text as="span" {...styles.warrantyName}>
                                                {name}
                                            </Text>{' '}
                                            <Text {...styles.price}>
                                                {'+ '}
                                                <FormattedNumber
                                                    style="currency"
                                                    currency={basket?.currency}
                                                    value={price || 0}
                                                />
                                            </Text>
                                        </Flex>
                                    </Flex>
                                </HStack>
                            )
                        }),
                    )}
                </Stack>
            </Flex>{' '}
        </>
    )
}

WarrantySectionCart.propTypes = {
    warranties: PropTypes.array.isRequired,
    product: PropTypes.object.isRequired,
    warrantyTermsAndConditionsLink: PropTypes.string,
}

export default WarrantySectionCart
